'use client';

import { useRouter } from '@bprogress/next';
import { HeartIcon } from '@c/icons';
import type { SvgIconProps } from '@c/icons/types';
import WishlistAction from '@c/toasts/WishlistAction';
import { useQueryClient } from '@tanstack/react-query';
import { getItem, logEvent } from '@util/analytics';
import {
  addProductToFavorites,
  removeProductFromFavorites,
} from '@util/firestore/products';
import {
  trackUserInteraction,
  type Attribution,
} from '@util/firestore/recommendations';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import { motion } from 'framer-motion';
import type { ProductLight } from 'models/product';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

export const FavoriteButton = ({
  product,
  initialCount = 0,
  attribution,
}: {
  product: ProductLight;
  initialCount?: number;
  attribution?: Attribution;
}) => {
  const likedProps: SvgIconProps = {
    pathProps: {
      stroke: 'none',
      fill: '#FF4242',
      className: 'hover:none',
    },
  };
  const defaultProps: SvgIconProps = {
    pathProps: {
      stroke: 'currentColor',
      fill: 'none',
    },
  };
  const [isLiked, setLiked] = useState(false);
  const [favoriteCount, setFavoriteCount] = useState(initialCount);

  const { userDoc } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const { showToast } = useToastContext();

  useEffect(() => {
    if (userDoc) {
      const liked = userDoc.favorites?.includes(product.id);
      setLiked(!!liked);
    }
  }, [product.id, userDoc]);

  const props = isLiked ? likedProps : defaultProps;

  const onButtonClick = () => {
    const wasLiked = isLiked;
    setLiked(!isLiked);

    if (userDoc) {
      if (wasLiked) {
        removeProductFromFavorites(userDoc.uid, product.id);
        setFavoriteCount(favoriteCount - 1);
        trackUserInteraction({
          uid: userDoc.uid,
          pid: product.id,
          interaction: 'unfavorite',
          attribution,
        });
      } else {
        addProductToFavorites(userDoc.uid, product.id);
        setFavoriteCount(favoriteCount + 1);
        trackUserInteraction({
          uid: userDoc.uid,
          pid: product.id,
          interaction: 'favorite',
          attribution,
        });
      }
      queryClient.invalidateQueries({
        queryKey: ['favorites', userDoc?.uid],
      });
      logEvent(
        'add_to_wishlist',
        {
          currency: 'USD',
          items: [getItem(product)],
        },
        userDoc?.uid
      );
      showToast(<WishlistAction removed={wasLiked} />);
    } else router.push(`/login?redirect=${encodeURI(pathname ?? '/')}`);
  };

  return (
    <motion.button
      type="button"
      aria-label="favorite"
      className="absolute z-10 h-16 w-16 rounded-full border border-zinc-100 bg-brand-white/70 p-3 backdrop-blur-lg transition-colors duration-150 ease-linear hover:bg-zinc-200"
      whileTap={{ scale: 0.85 }}
      onClick={(e) => {
        e.preventDefault();
        onButtonClick();
      }}
    >
      <HeartIcon className="h-full w-full" {...props} />
    </motion.button>
  );
};
